import { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { usePrepareFormTagsForSetReportPayload } from 'hooks/usePrepareFormTagsForSetReportPayload';

import { useQueryGetReport } from 'api/queries/getReport.query';

import { UseReportSettingsModalProvider } from 'components/Dashboards/Reporting/ReportSettingsModal/ReportSettingsModal.provider';
import { FormValues } from 'components/Dashboards/Reporting/ReportSettingsModal/ReportSettingsModal.types';
import {
    convertFormDataToAPIPayload,
    getInitialFormValues,
} from 'components/Dashboards/Reporting/ReportSettingsModal/ReportSettingsModal.utils';
import { ReportPageUrlParams } from 'components/pages/ReportPage/ReportPage';

export type UseReportSettingsModalLogic = ReturnType<typeof useReportSettingsModalLogic>;

interface Props {
    api: UseReportSettingsModalProvider['api'];
    setIsModalOpen: (value: boolean) => void;
}

export const useReportSettingsModalLogic = ({ api, setIsModalOpen }: Props) => {
    const reportId = Number(useParams<ReportPageUrlParams>().reportId);
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { getTagsPayloadForReport } = usePrepareFormTagsForSetReportPayload();

    const queryClient = useQueryClient();
    const { data: report } = useQueryGetReport({ id: reportId });
    const { data: tags, isSuccess: isTagsLoaded } = api.getReportsTags;
    const { data: reportAccessData, isSuccess: isReportAccessDataLoaded } = api.getAccessStatus;
    const { mutateAsync: setReportAccessStatus, isLoading: isSettingReportStatus } = api.setReportAccessStatus.instance;
    const methods = useForm<FormValues>({
        defaultValues: {
            selectedTags: [],
            userAccessList: [],
            notifyAllUsersOnUpload: false,
            allUsersAccess: false,
            accessListOption: 'whole',
        },
    });

    const isFormReady = isTagsLoaded && isReportAccessDataLoaded;

    const {
        handleSubmit,
        formState: { isDirty },
    } = methods;

    const onSubmit = async (submittedData: FormValues) => {
        const reportTagsPayload = await getTagsPayloadForReport(submittedData.selectedTags);

        const formData = { ...submittedData, selectedTags: reportTagsPayload };
        const payload = convertFormDataToAPIPayload(formData, reportId);

        try {
            await setReportAccessStatus(payload, {
                onSuccess: async () => {
                    sendEventWithDimensions({
                        category: 'Reporting',
                        action: 'Report Shared',
                        label: report?.name || 'Unknown',
                    });
                    setIsModalOpen(false);
                    await Promise.all([
                        queryClient.invalidateQueries('getAllReportTags'),
                        queryClient.invalidateQueries('getReportList'),
                        queryClient.invalidateQueries({
                            queryKey: ['getReport', { id: reportId }],
                        }),
                        queryClient.invalidateQueries({
                            queryKey: ['getReportAccessStatus', { id: reportId }],
                        }),
                    ]);
                    handleSnackBar('Report settings updated successfully', 'success');
                    methods.reset({ ...formData }, { keepDirty: false, keepTouched: false });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        } catch (error) {
            DEFAULT_ERROR({ error, handleError: handleSnackBar });
        }
    };

    const onModalClose = () => {
        setIsModalOpen(false);
        methods.reset();
    };

    const canSubmit = isDirty && !isSettingReportStatus;

    useEffect(() => {
        if (isFormReady && tags) {
            const initialValues = getInitialFormValues(tags, reportAccessData);
            methods.reset(initialValues, {
                keepTouched: false,
                keepDirty: false,
            });
        }
    }, [isFormReady, tags, methods, reportAccessData]);

    return {
        logic: {
            onModalClose,
            methods,
            handleSubmit,
            onSubmit,
            canSubmit,
        },
    };
};
